/**
 * Canonical UI release metadata — injected at deploy time into index.html.
 * Never hard-code release values in React components.
 * Never invent a git SHA from the current checkout / main branch.
 */

export type ReleaseEnvironment = "staging" | "production" | "development" | "unknown";

export interface ReleaseMetadata {
  /** e.g. v2.20260727-083531 */
  releaseId: string;
  /** Short git SHA of the exact UI commit deployed */
  gitSha: string;
  /** UTC timestamp string, e.g. 2026-07-27 08:35:31 UTC */
  buildTimestamp: string;
  environment: ReleaseEnvironment;
  /** True when placeholders were not replaced (local/dev) */
  isPlaceholder: boolean;
}

declare global {
  interface Window {
    __BUILD_VERSION?: string;
    __BUILD_TIMESTAMP?: string;
    __KK_RELEASE_ID?: string;
    __KK_GIT_SHA?: string;
    __KK_BUILD_TIMESTAMP?: string;
    __KK_ENVIRONMENT?: string;
  }
}

const PLACEHOLDER_RE = /^%[A-Z0-9_]+%$/;
const MALFORMED_HINTS = /^(undefined|null|unknown|local|localhost)$/i;

function readRaw(value: string | undefined): string {
  if (value == null) return "";
  const trimmed = String(value).trim();
  if (!trimmed || PLACEHOLDER_RE.test(trimmed)) return "";
  if (MALFORMED_HINTS.test(trimmed)) return "";
  return trimmed;
}

function normalizeEnvironment(raw: string): ReleaseEnvironment {
  const v = raw.toLowerCase();
  if (v === "staging" || v === "production" || v === "development") return v;
  const fromEnv = String(process.env.REACT_APP_ENV || "").toLowerCase();
  if (fromEnv === "staging" || fromEnv === "production" || fromEnv === "development") {
    return fromEnv;
  }
  return "unknown";
}

/**
 * Resolve release metadata from window globals (deploy inject) with safe
 * localhost fallbacks. Does not invent a git SHA from the current checkout.
 */
export function getReleaseMetadata(
  win: Window = typeof window !== "undefined" ? window : ({} as Window),
): ReleaseMetadata {
  const releaseId =
    readRaw(win.__KK_RELEASE_ID) ||
    (readRaw(win.__BUILD_VERSION) ? `v2.${readRaw(win.__BUILD_VERSION)}` : "");
  const gitSha = readRaw(win.__KK_GIT_SHA);
  const buildTimestamp =
    readRaw(win.__KK_BUILD_TIMESTAMP) || readRaw(win.__BUILD_TIMESTAMP);
  const environment = normalizeEnvironment(readRaw(win.__KK_ENVIRONMENT));

  const isPlaceholder = !releaseId || !gitSha || !buildTimestamp;

  if (isPlaceholder) {
    return {
      releaseId: releaseId || "v2.uninjected (localhost)",
      gitSha: gitSha || "uninjected",
      buildTimestamp: buildTimestamp || "",
      environment:
        environment === "unknown"
          ? normalizeEnvironment(String(process.env.REACT_APP_ENV || "development"))
          : environment,
      isPlaceholder: true,
    };
  }

  return {
    releaseId: releaseId.startsWith("v") ? releaseId : `v${releaseId}`,
    gitSha,
    buildTimestamp,
    environment,
    isPlaceholder: false,
  };
}

/** Fail closed helper for tests: production builds must not report staging. */
export function assertEnvironmentMatchesBuild(
  meta: ReleaseMetadata,
  expected: ReleaseEnvironment,
): boolean {
  if (meta.isPlaceholder) return expected === "development" || expected === "unknown";
  return meta.environment === expected;
}

/**
 * Guard used by tests / deploy smoke: a production build must never surface
 * staging release fields.
 */
export function assertProductionExcludesStaging(meta: ReleaseMetadata): boolean {
  if (meta.environment !== "production" || meta.isPlaceholder) return true;
  const blob = `${meta.releaseId} ${meta.gitSha} ${meta.buildTimestamp} ${meta.environment}`.toLowerCase();
  return !blob.includes("staging");
}
